import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import Divider from "@mui/material/Divider";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { MainMenuList, ComplianceMenuList, AnalysesMenuList, ToolsMenuGroups, SettingsMenuGroups } from "./MenuList";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import { signOut } from "firebase/auth";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Toolbar from "@mui/material/Toolbar";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button, Collapse, ListItemIcon, Typography, Badge } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import Swal from "sweetalert2";
import { AUTH_LOGIN } from '../../Config/firebase';
import LOGO from "../../Assets/Images/Common/logo.png";
import LOGOUT from '../../Assets/Images/Common/Logout.png';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import NotificationBell from '../../Components/NotificationBell/NotificationBell';
import { usePendingTransactions } from '../../hooks/usePendingTransactions';
const drawerWidth = 280;

const isActivePath = (pathname, path) => pathname.includes(path);

const menuButtonSx = (active) => ({
  background: active
    ? "linear-gradient(135deg, #2af598 0%, #00ba2d 100%)"
    : "transparent",
  padding: '8px 12px',
  borderRadius: '8px',
  margin: '4px 8px',
  position: 'relative',
  "&:hover": {
    background: "linear-gradient(135deg, #3adecc 0%, #2af598 100%)",
    boxShadow: "0 4px 12px rgba(42, 245, 152, 0.3)",
  }
});

const menuTextSx = (active) => ({
  color: active ? "#000000" : "#353535",
  pl: 0.5,
  ".MuiTypography-root": {
    fontWeight: "500",
    fontSize: "16px",
    fontFamily: "Helvetica Neue",
  }
});

const SectionHeader = ({ label, extraTop = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center', paddingTop: extraTop ? '24px' : 0 }}>
    <Divider sx={{ flexGrow: 1, borderColor: '#ADADAD' }} />
    <Typography sx={{ color: "#ADADAD", fontSize: "16px", mx: 1 }}>{label}</Typography>
    <Divider sx={{ flexGrow: 1, borderColor: '#ADADAD' }} />
  </Box>
);

const SidebarMenuItem = ({ title, path, icon, active, badgeCount = 0, pathname, indent = false }) => {
  const selected = isActivePath(pathname, path);
  return (
    <ListItem disablePadding sx={{ m: indent ? "0.15rem 0" : "0.5rem 0" }}>
      <Link
        style={{ color: "black", textDecoration: "none", width: "100%" }}
        to={path}
      >
        <ListItemButton sx={{ ...menuButtonSx(selected), ...(indent ? { marginLeft: '16px', marginRight: '8px' } : {}) }}>
          <ListItemIcon sx={{ minWidth: "34px", pl: 0.5 }}>
            {selected ? active : icon}
          </ListItemIcon>
          <ListItemText primary={title} sx={menuTextSx(selected)} />
          {badgeCount > 0 && (
            <Badge
              badgeContent={badgeCount}
              color="error"
              sx={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 4px',
                  fontWeight: 'bold',
                  right: 0,
                  top: 0
                }
              }}
            >
              <Box sx={{ width: 0, height: 0 }} />
            </Badge>
          )}
        </ListItemButton>
      </Link>
    </ListItem>
  );
};

const SidebarMenuGroup = ({ group, pathname, open, onToggle }) => {
  const childActive = group.items.some((entry) => isActivePath(pathname, entry.path));
  return (
    <Box>
      <ListItem disablePadding sx={{ m: "0.15rem 0" }}>
        <ListItemButton
          onClick={onToggle}
          sx={{
            padding: '6px 12px',
            borderRadius: '8px',
            margin: '2px 8px',
            "&:hover": { background: "rgba(255,255,255,0.06)" },
          }}
        >
          <ListItemText
            primary={group.title}
            sx={{
              color: childActive ? "#2af598" : "#ADADAD",
              ".MuiTypography-root": {
                fontWeight: childActive ? 600 : 500,
                fontSize: "13px",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontFamily: "Helvetica Neue",
              }
            }}
          />
          {open ? (
            <ExpandLessIcon sx={{ fontSize: 18, color: childActive ? "#2af598" : "#ADADAD" }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 18, color: childActive ? "#2af598" : "#ADADAD" }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <List sx={{ p: 0 }}>
          {group.items.map((entry) => (
            <SidebarMenuItem
              key={entry.path}
              {...entry}
              pathname={pathname}
              indent
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};

const Sidebar = (props) => {
  const { element } = props;
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const location = useLocation();
  const { pendingDeposits, pendingWithdrawals } = usePendingTransactions();

  const allGroups = [...ToolsMenuGroups, ...SettingsMenuGroups];
  const [openGroups, setOpenGroups] = React.useState(() => {
    const initial = {};
    allGroups.forEach((group) => {
      initial[group.id] = group.items.some((entry) =>
        window.location.pathname.includes(entry.path)
      );
    });
    return initial;
  });

  React.useEffect(() => {
    setOpenGroups((prev) => {
      const next = { ...prev };
      allGroups.forEach((group) => {
        if (group.items.some((entry) => location.pathname.includes(entry.path))) {
          next[group.id] = true;
        }
      });
      return next;
    });
  }, [location.pathname]);

  const toggleGroup = (id) => {
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));
  };
  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };
  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure you want to logout?",
      text: "Logging out will end your session and you will be redirected to the login page.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#46adf2",
      cancelButtonColor: "#5b5d5d",
      confirmButtonText: "Yes, logout",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await signOut(AUTH_LOGIN);
        localStorage.clear();
        Swal.fire({
          title: "Logged Out",
          text: "You have been successfully logged out. Redirecting to login page...",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          navigate("/login");
        });
      }
    });
  };
  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const drawer = (
    <Box
      sx={{
        background: "linear-gradient(167.96deg, #353535 0%, #000000 100%)",
        height: "100vh",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexDirection: "column",
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pt: 3,
            px: 2,
            width: '100%'
          }}
        >
          <Box sx={{ width: '200px' }}>
            <img
              src={LOGO}
              alt="logo"
              style={{
                height: "100%",
                width: "100%",
              }}
            />
          </Box>
          <NotificationBell />
        </Box>
      </Box>
      <Box
        sx={{ width: "100%", height: "calc(100vh - 150px)", overflowY: "auto", paddingTop: "20px" }}
        className="customSidebarList"
      >
        <SectionHeader label="MAIN" />
        <List sx={{ p: 0, m: 0.3 }}>
          {MainMenuList.map((entry) => {
            let badgeCount = 0;
            if (entry.path === '/admin/fundsdeposit' && pendingDeposits > 0) {
              badgeCount = pendingDeposits;
            } else if (entry.path === '/admin/withdrawfunds' && pendingWithdrawals > 0) {
              badgeCount = pendingWithdrawals;
            }
            return (
              <SidebarMenuItem
                key={entry.path}
                {...entry}
                pathname={location.pathname}
                badgeCount={badgeCount}
              />
            );
          })}
        </List>

        <SectionHeader label="COMPLIANCE" extraTop />
        <List sx={{ p: 0, m: 0.3 }}>
          {ComplianceMenuList.map((entry) => (
            <SidebarMenuItem key={entry.path} {...entry} pathname={location.pathname} />
          ))}
        </List>

        <SectionHeader label="ANALYSES" extraTop />
        <List sx={{ p: 0, m: 0.3 }}>
          {AnalysesMenuList.map((entry) => (
            <SidebarMenuItem key={entry.path} {...entry} pathname={location.pathname} />
          ))}
        </List>

        <SectionHeader label="TOOLS" extraTop />
        <List sx={{ p: 0, m: 0.3 }}>
          {ToolsMenuGroups.map((group) => (
            <SidebarMenuGroup
              key={group.id}
              group={group}
              pathname={location.pathname}
              open={!!openGroups[group.id]}
              onToggle={() => toggleGroup(group.id)}
            />
          ))}
        </List>

        <SectionHeader label="SETTINGS" extraTop />
        <List sx={{ p: 0, m: 0.3 }}>
          {SettingsMenuGroups.map((group) => (
            <SidebarMenuGroup
              key={group.id}
              group={group}
              pathname={location.pathname}
              open={!!openGroups[group.id]}
              onToggle={() => toggleGroup(group.id)}
            />
          ))}
        </List>
      </Box>
      {/* {/ Logout Button /} */}
      <Button
        type="button"
        sx={{
          width: "100%",
          background:
            "transparent",
          padding: "8px 12px",
          fontWeight: "500",
          fontSize: "18px",
          color: "#737373",
          textTransform: "capitalize",
          position: "absolute",
          bottom: "0px",
          left: "0px",
          borderRadius: "0px",
          "&:hover": {
            background:
              "transparent",
            color: "#737373",
          },

        }}
        onClick={() => handleLogout()}
      >
        <img src={LOGOUT} alt="" /> Logout
      </Button>
      <Box
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "center",
          width: "100%",
          height: "50px",
          boxSizing: "border-box",
        }}
      >
      </Box>

      <Divider />
    </Box >
  );

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          display: { sm: "none" },
          background: "linear-gradient(148.97deg, #0C2C50 0%, #450F5B 100%)",
          boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.1)",
          zIndex: 1000,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MoreVertIcon sx={{fontSize:'32px'}} />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onTransitionEnd={handleDrawerTransitionEnd}
          onClose={handleDrawerClose}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", sm: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: drawerWidth,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
    </Box>
  );
}

export default Sidebar;
